import { useState, useEffect, useRef, useCallback } from 'react';
import { useLetterheadExport } from '../reports/useLetterheadExport';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Plus, Download, Pencil, Trash2, Save, X, ClipboardList } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PdfFormOverlay({ template, formValues, setFormValues, pageNum }) {
  const [pageUrl, setPageUrl] = useState(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const imgRef = useRef(null);

  useEffect(() => {
    let objectUrl = null;
    const loadPage = async () => {
      try {
        const res = await axios.get(`${API}/pdf-templates/${template.id}/page/${pageNum}`, { responseType: 'blob' });
        objectUrl = URL.createObjectURL(res.data);
        setPageUrl(objectUrl);
      } catch {
        setPageUrl(null);
      }
    };
    loadPage();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [template.id, pageNum]);

  const handleImageLoad = (e) => {
    setImgSize({ width: e.target.naturalWidth, height: e.target.naturalHeight });
  };

  const pageFields = (template.fields || []).filter(f => (f.page || 0) === pageNum);
  const pageDims = template.page_dims?.[pageNum];

  return (
    <div className="relative w-full" style={{ maxWidth: '100%' }}>
      {pageUrl ? (
        <img
          ref={imgRef}
          src={pageUrl}
          alt={`Page ${pageNum + 1}`}
          className="w-full h-auto border border-slate-200 rounded"
          onLoad={handleImageLoad}
          draggable={false}
        />
      ) : (
        <div className="w-full h-64 bg-slate-100 flex items-center justify-center text-slate-400 rounded border">
          Loading page {pageNum + 1}...
        </div>
      )}
      {/* Overlay fields on top of the image */}
      {pageUrl && pageDims && imgRef.current && pageFields.map((field) => {
        if (!field.rect) return null;
        const [x1, y1, x2, y2] = field.rect;
        const ph = field.page_height || pageDims.height;
        const pw = pageDims.width;
        
        const displayWidth = imgRef.current.clientWidth;
        const scale = displayWidth / pw;
        
        // PDF coords: bottom-left origin. Convert to top-left.
        const left = x1 * scale;
        const top = (ph - y2) * scale;
        const width = (x2 - x1) * scale;
        const height = (y2 - y1) * scale;

        if (field.field_type === 'checkbox') {
          return (
            <div
              key={field.name}
              className="absolute"
              style={{ left: `${left}px`, top: `${top}px`, width: `${Math.max(width, 18)}px`, height: `${Math.max(height, 18)}px` }}
            >
              <Checkbox
                checked={formValues[field.name] === 'Yes' || formValues[field.name] === '/Yes' || formValues[field.name] === true}
                onCheckedChange={(checked) => setFormValues({ ...formValues, [field.name]: checked ? 'Yes' : 'Off' })}
                className="w-full h-full bg-white/80 border-blue-400"
                data-testid={`overlay-field-${field.name}`}
              />
            </div>
          );
        }

        return (
          <input
            key={field.name}
            type="text"
            value={formValues[field.name] || ''}
            onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
            className="absolute bg-white/80 border border-blue-300 rounded-sm text-xs px-1 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:bg-white"
            style={{ left: `${left}px`, top: `${top}px`, width: `${Math.max(width, 40)}px`, height: `${Math.max(height, 18)}px`, fontSize: `${Math.max(height * 0.6, 10)}px` }}
            data-testid={`overlay-field-${field.name}`}
          />
        );
      })}
    </div>
  );
}

export default function JobFormsSection({ jobId, canModify }) {
  const { requestExport, exportFeedback, exporting } = useLetterheadExport('form-letterhead-export');
  const [templates, setTemplates] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showAttach, setShowAttach] = useState(false);
  const [fillForm, setFillForm] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();

  const loadForms = useCallback(async () => {
    try {
      const [formsRes, allRes] = await Promise.all([
        axios.get(`${API}/jobs/${jobId}/pdf-forms`),
        axios.get(`${API}/pdf-templates`, { params: { division: 'shop' } })
      ]);
      setTemplates(formsRes.data.templates || []);
      setSubmissions(formsRes.data.submissions || []);
      setAllTemplates(allRes.data || []);
    } catch { toast.error('Failed to load forms'); }
  }, [jobId]);

  useEffect(() => { loadForms(); }, [loadForms]);

  const handleAttach = async (templateId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/attach-template`, { template_id: templateId });
      toast.success('Form attached to job');
      setShowAttach(false);
      loadForms();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to attach form'); }
  };

  const handleDetach = async (templateId) => {
    try {
      await axios.delete(`${API}/jobs/${jobId}/detach-template/${templateId}`);
      toast.success('Form removed from job');
      loadForms();
    } catch { toast.error('Failed to remove form'); }
  };

  const openFillForm = (template, existingSubmission) => {
    const initialValues = {};
    if (existingSubmission) {
      Object.assign(initialValues, existingSubmission.fields_data);
    } else {
      // Start with empty values for new forms (ignore PDF default values)
      (template.fields || []).forEach(f => {
        initialValues[f.name] = '';
      });
    }
    setFormValues(initialValues);
    setFillForm({ template, submission: existingSubmission || null });
  };

  const handleSubmitForm = async () => {
    if (!fillForm) return;
    setSaving(true);
    try {
      if (fillForm.submission) {
        await axios.patch(`${API}/pdf-submissions/${fillForm.submission.id}`, { fields_data: formValues });
        toast.success('Form saved');
      } else {
        await axios.post(`${API}/jobs/${jobId}/pdf-submissions`, { template_id: fillForm.template.id, fields_data: formValues });
        toast.success('Form submitted');
      }
      setFillForm(null);
      loadForms();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save form'); }
    finally { setSaving(false); }
  };

  const handleDownload = async (submission) => {
    try {
      toast.info('Generating PDF...');
      const response = await axios.get(`${API}/pdf-submissions/${submission.id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${submission.template_name}_${submission.filled_by_name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) { throw error; }
  };

  const handleDeleteSubmission = async (submission) => {
    try {
      await axios.delete(`${API}/pdf-submissions/${submission.id}`);
      toast.success('Submission deleted');
      loadForms();
    } catch { toast.error('Failed to delete submission'); }
  };

  const attachedIds = templates.map(t => t.id);
  const availableTemplates = allTemplates.filter(t => !attachedIds.includes(t.id));
  const hasVisualOverlay = (template) => template.page_count > 0 && template.page_dims?.length > 0;

  return (
    <Card className="p-6 bg-white border border-slate-200 shadow-sm" data-testid="job-forms-section">
      {exportFeedback}
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          <ClipboardList size={16} /> Forms
        </h4>
        {canModify && availableTemplates.length > 0 && (
          <Dialog open={showAttach} onOpenChange={setShowAttach}>
            <Button variant="outline" size="sm" onClick={() => setShowAttach(true)} data-testid="attach-form-btn">
              <Plus size={14} className="mr-1" /> Attach Form
            </Button>
            <DialogContent>
              <DialogHeader><DialogTitle>Attach Form Template</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-80 overflow-y-auto" data-testid="attach-form-list">
                {availableTemplates.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-3 rounded-md border border-slate-200 hover:border-slate-400 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText size={18} className="text-slate-500" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{t.name}</p>
                        <p className="text-xs text-slate-400">{t.fields?.length || 0} fields, {t.page_count || 1} page(s)</p>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => handleAttach(t.id)} data-testid={`attach-template-${t.id}`}>Attach</Button>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {templates.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4">No forms attached to this job</p>
      ) : (
        <div className="space-y-3">
          {templates.map(template => {
            const templateSubmissions = submissions.filter(s => s.template_id === template.id);
            return (
              <div key={template.id} className="border border-slate-200 rounded-md" data-testid={`form-template-${template.id}`}>
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-t-md">
                  <div className="flex items-center gap-3">
                    <FileText size={18} className="text-slate-600" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{template.name}</p>
                      <p className="text-xs text-slate-400">{template.fields?.length || 0} fields, {template.page_count || 1} page(s)</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openFillForm(template, null)} data-testid={`fill-form-${template.id}`}>
                      <Pencil size={14} className="mr-1" /> Fill Out
                    </Button>
                    {canModify && (
                      <Button variant="outline" size="sm" onClick={() => handleDetach(template.id)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`detach-form-${template.id}`}>
                        <X size={14} />
                      </Button>
                    )}
                  </div>
                </div>
                {templateSubmissions.length > 0 && (
                  <div className="p-3 space-y-2">
                    {templateSubmissions.map(sub => (
                      <div key={sub.id} className="flex items-center justify-between p-3 bg-white border border-slate-100 rounded" data-testid={`submission-${sub.id}`}>
                        <div>
                          <p className="text-sm text-slate-700">Filled by <span className="font-medium">{sub.filled_by_name}</span></p>
                          <p className="text-xs text-slate-400">{new Date(sub.created_at).toLocaleString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => openFillForm(template, sub)} data-testid={`edit-submission-${sub.id}`}>
                            <Pencil size={12} className="mr-1" /> Edit
                          </Button>
                          <Button variant="outline" size="sm" disabled={exporting} onClick={() => requestExport(() => handleDownload(sub))} data-testid={`download-submission-${sub.id}`}>
                            <Download size={12} className="mr-1" /> PDF
                          </Button>
                          {(sub.filled_by === user?.id || user?.role === 'management') && (
                            <Button variant="outline" size="sm" onClick={() => handleDeleteSubmission(sub)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`delete-submission-${sub.id}`}>
                              <Trash2 size={12} />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Fill Form Dialog - Visual PDF Overlay or Simple Form */}
      <Dialog open={!!fillForm} onOpenChange={(open) => { if (!open) setFillForm(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{fillForm?.submission ? 'Edit' : 'Fill Out'}: {fillForm?.template?.name}</DialogTitle>
          </DialogHeader>
          {fillForm && (
            <div data-testid="fill-form-dialog">
              {hasVisualOverlay(fillForm.template) ? (
                /* Visual PDF overlay mode */
                <div className="space-y-4">
                  {Array.from({ length: fillForm.template.page_count || 1 }, (_, i) => (
                    <PdfFormOverlay
                      key={i}
                      template={fillForm.template}
                      formValues={formValues}
                      setFormValues={setFormValues}
                      pageNum={i}
                    />
                  ))}
                  <div className="sticky bottom-0 bg-white pt-3 border-t border-slate-200 flex gap-3">
                    <Button onClick={handleSubmitForm} disabled={saving} className="flex-1 bg-slate-900" data-testid="submit-form-btn">
                      <Save size={16} className="mr-2" />
                      {saving ? 'Saving...' : (fillForm.submission ? 'Save Changes' : 'Submit Form')}
                    </Button>
                    <Button variant="outline" onClick={() => setFillForm(null)} className="flex-1" data-testid="cancel-form-btn">Cancel</Button>
                  </div>
                </div>
              ) : (
                /* Fallback: simple form for templates without page data */
                <div className="space-y-4">
                  {(fillForm.template.fields || []).length === 0 ? (
                    <p className="text-sm text-slate-500 py-4 text-center">This PDF has no extractable form fields.</p>
                  ) : (
                    (fillForm.template.fields || []).map((field) => (
                      <div key={field.name} className="space-y-1.5">
                        <label className="text-sm font-medium text-slate-700">{field.name.replace(/_/g, ' ')}</label>
                        {field.field_type === 'checkbox' ? (
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={formValues[field.name] === 'Yes' || formValues[field.name] === '/Yes'}
                              onCheckedChange={(checked) => setFormValues({ ...formValues, [field.name]: checked ? 'Yes' : 'Off' })}
                            />
                            <span className="text-sm text-slate-600">{formValues[field.name] === 'Yes' ? 'Checked' : 'Unchecked'}</span>
                          </div>
                        ) : (
                          <input
                            type="text"
                            value={formValues[field.name] || ''}
                            onChange={(e) => setFormValues({ ...formValues, [field.name]: e.target.value })}
                            className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                            placeholder={`Enter ${field.name.replace(/_/g, ' ')}`}
                          />
                        )}
                      </div>
                    ))
                  )}
                  <div className="flex gap-3 pt-4 border-t border-slate-200">
                    <Button onClick={handleSubmitForm} disabled={saving} className="flex-1 bg-slate-900" data-testid="submit-form-btn">
                      <Save size={16} className="mr-2" />
                      {saving ? 'Saving...' : (fillForm.submission ? 'Save Changes' : 'Submit Form')}
                    </Button>
                    <Button variant="outline" onClick={() => setFillForm(null)} className="flex-1" data-testid="cancel-form-btn">Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
